// First-party, cookie-free page-view tracking for the public website.
//
// Privacy notes:
//  - `sm_vid` (localStorage) and `sm_sid` (sessionStorage) hold a random,
//    anonymous id only. No email, name, token or message content is ever stored
//    or transmitted here; `user_id` is an opaque account id supplied by the app.
//  - Storage access can legitimately throw (private mode, blocked storage,
//    iframes). Those cases fall back to an in-memory id, which is expected -
//    but they are reported through the dev-only logger rather than swallowed.
//  - Tracking must never break the page, so network failures are caught and
//    logged in development only.
import { devWarn } from "./logger";

const BASE = process.env.REACT_APP_BACKEND_URL;

const VISITOR_KEY = "sm_vid";
const SESSION_KEY = "sm_sid";

function rand() {
  try {
    return crypto.randomUUID();
  } catch (err) {
    // Older browsers / insecure contexts do not expose randomUUID.
    devWarn("analytics.randomUUID", err);
    return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  }
}

// Fallback ids kept in memory when storage is unavailable, so a single visit is
// still counted consistently instead of generating a new id on every event.
let memoryVisitorId = null;
let memorySessionId = null;

function getVisitor() {
  let isNew = false;
  let id = null;
  try {
    id = localStorage.getItem(VISITOR_KEY);
    if (!id) {
      id = rand();
      localStorage.setItem(VISITOR_KEY, id);
      isNew = true;
    }
  } catch (err) {
    devWarn("analytics.visitorStorageUnavailable", err);
    if (!memoryVisitorId) {
      memoryVisitorId = rand();
      isNew = true;
    }
    id = memoryVisitorId;
  }
  return { id, isNew };
}

function getSession() {
  try {
    let sid = sessionStorage.getItem(SESSION_KEY);
    if (!sid) {
      sid = rand();
      sessionStorage.setItem(SESSION_KEY, sid);
    }
    return sid;
  } catch (err) {
    devWarn("analytics.sessionStorageUnavailable", err);
    if (!memorySessionId) memorySessionId = rand();
    return memorySessionId;
  }
}

let lastPath = null;

export function trackEvent(name, meta = {}) {
  if (!BASE || !name) return;
  const { id } = getVisitor();
  const body = JSON.stringify({
    name,
    visitor_id: id,
    session_id: getSession(),
    path: window.location.pathname,
    ...meta,
  });
  fetch(`${BASE}/api/track/event`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch((err) => devWarn("analytics.trackEvent", err));
}

export function trackPageView(path, userId = null) {
  if (!BASE || !path) return;
  if (path === lastPath) return;
  lastPath = path;

  const { id, isNew } = getVisitor();
  const body = JSON.stringify({
    path,
    referrer: document.referrer || null,
    visitor_id: id,
    session_id: getSession(),
    screen_width: window.innerWidth || null,
    is_new_visitor: isNew,
    user_id: userId,
  });

  const url = `${BASE}/api/track`;
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon(url, new Blob([body], { type: "application/json" }));
      return;
    }
  } catch (err) {
    // Some browsers throw if the payload is rejected; fall through to fetch.
    devWarn("analytics.sendBeacon", err);
  }
  fetch(url, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body,
    keepalive: true,
  }).catch((err) => devWarn("analytics.trackPageView", err));
}
